import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { BottomTabBar } from './BottomTabBar';
import { MoreMenuSheet } from './MoreMenuSheet';
import { MobileSearchOverlay } from './MobileSearchOverlay';
import { cn } from '../../utils/cn'
import { wsService, subscriptions } from '../../services/websocket-unified'
import toast, { Toaster } from 'react-hot-toast';
import { Menu, X } from 'lucide-react';
import { useMobileNavigation } from '../../hooks/useMobileNavigation'
import { useAuth } from '../../contexts/AuthContext';

export const Layout: React.FC = () => {
  const { currentUser } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const {
    moreMenuOpen,
    openMoreMenu,
    closeMoreMenu,
    searchOpen,
    openSearch,
    closeSearch
  } = useMobileNavigation();

  // Handle WebSocket connection with Chrome-specific optimization
  useEffect(() => {
    const connectWebSocket = async () => {
      // Check if already connected
      if (wsService.isConnected()) {
        console.log('[LAYOUT] connectWebSocket call initiated');
        setWsConnected(true);
        return;
      }
      
      console.log('[LAYOUT] connectWebSocket call initiated');
      
      try {
        await wsService.connect();
        setWsConnected(true);
        console.log('[LAYOUT] WebSocket connected successfully');
        console.log('[LAYOUT] About to show toast notification...');
        toast.success('Real-time connection established', {
          duration: 3000,
          position: 'bottom-right',
        });
        console.log('[LAYOUT] Toast notification shown');
      } catch (error) {
        console.error('[LAYOUT] WebSocket connection failed:', error);
        setWsConnected(false);
        toast.error('Real-time connection failed', {
          duration: 5000,
          position: 'bottom-right',
        });
      }
    };

    // connect() rejects on the first connect_error while the socket keeps
    // reconnecting, and a live socket can drop later; the banner follows the
    // socket's own connect and disconnect from here on.
    const unsubscribe = subscriptions.onConnectionStatus((status) => {
      setWsConnected(status.connected);
    });

    // Connect immediately for all browsers - no Chrome-specific logic needed
    connectWebSocket();
    return () => {
      unsubscribe();
      wsService.disconnect();
    };
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle sidebar with Cmd/Ctrl + B
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        setSidebarCollapsed(!sidebarCollapsed);
      }

      // Search with Cmd/Ctrl + K
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        // Focus search input
        const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sidebarCollapsed]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
        // Close mobile menu on resize to desktop
        if (window.innerWidth >= 1024) {
          setMobileMenuOpen(false);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-console-bg text-console-text">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-nav-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Button - Hidden on small mobile, visible on tablets */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="mobile-menu-button touch-target-icon fixed top-4 left-4 z-50 hidden sm:block lg:hidden"
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />

      {/* Header */}
      <Header 
        sidebarCollapsed={sidebarCollapsed}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        onMobileSearchOpen={openSearch}
      />

      {/* Main Content */}
      <main
        className={cn(
          'transition-all duration-300 pt-16 min-h-screen',
          'ml-0', // No margin on mobile
          'lg:ml-64', // Add margin on desktop
          sidebarCollapsed && 'lg:ml-16',
          'pb-20 lg:pb-0', // Add bottom padding on mobile for bottom nav
        )}
      >
        <div className="px-4 pt-4 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        containerStyle={{
          bottom: 80, // Account for bottom navigation on mobile
        }}
        toastOptions={{
          className: 'bg-console-surface border border-console-border text-console-text',
          duration: 4000,
          style: {
            background: '#1a1f2e',
            color: '#e2e8f0',
            border: '1px solid #2d3748',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#1a1f2e',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#1a1f2e',
            },
          },
        }}
      />

      {/* Connection Status Indicator */}
      {!wsConnected && (
        <div className="fixed bottom-20 lg:bottom-4 left-4 z-50">
          <div className="bg-warning-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-sm font-medium">Reconnecting...</span>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar - Mobile Only */}
      <BottomTabBar onMoreClick={openMoreMenu} />

      {/* More Menu Sheet */}
      <MoreMenuSheet
        isOpen={moreMenuOpen}
        onClose={closeMoreMenu}
        userRole="viewer"
      />

      {/* Mobile Search Overlay */}
      <MobileSearchOverlay
        isOpen={searchOpen}
        onClose={closeSearch}
      />
    </div>
  );
};